import React, { useState, useRef, useCallback } from 'react';
import { useStoreWithEqualityFn } from 'zustand/traditional'; // Per selezionare dallo store
import { shallow } from 'zustand/shallow'; // Per selezione shallow
import { Upload, Loader2, Cloud, HardDrive } from 'lucide-react'; // Icone necessarie, Link rimosso
import Papa from 'papaparse';
import useGeoData from '../../../store/useGeoData';
import { processFiles } from '../hooks/useFileProcessor'; // Assicurati che il percorso sia corretto
import { Tabs } from '../../../common/Tabs'; // Import del nostro componente Tabs
import { MenuButton } from '../../../common/MenuButton'; // Import del nuovo MenuButton
import type { TabItem } from '../../../common/Tabs'; // Import del tipo TabItem corretto
// FeedbackBox rimosso
import { SelectInput, type SelectOption } from '../../../common/inputs/SelectInput/SelectInput'; // Import del componente SelectInput
// UrlInput rimosso
import AddRasterLayerDialog from '../../right-panel/components/dialogs/AddRasterLayerDialog'; // Importa il dialogo Raster
import AddVectorLayerDialog from '../../right-panel/components/dialogs/AddVectorLayerDialog'; // Importa il dialogo Vector
import type { CsvProcessingOptions } from '../types'; // Importa il nuovo tipo condiviso
import type { LayerBase, GeoDataState } from '../../../store/types'; // Importa LayerBase, GeoDataState e BBox
// OgcServiceType, OgcLayerInfo rimossi
// RasterLayerSpecification, AddLayerObject rimosse

import styles from './ImportPanel.module.css'; // Importa i CSS Modules
// feedbackBoxStyles rimosso

interface ImportPanelProps {
  // Potrebbero essere necessarie props per l'accordion in futuro, ma non per la logica di importazione
}

const ImportPanel: React.FC<ImportPanelProps> = () => {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [selectedLayerType, setSelectedLayerType] = useState<LayerBase['maplibreLayerType']>('fill'); // Default a 'fill' - Usa il tipo corretto
  const [csvFileHeaders, setCsvFileHeaders] = useState<string[] | null>(null); // Stato per gli header del file CSV
  const [csvOptions, setCsvOptions] = useState<CsvProcessingOptions | undefined>(undefined); // Stato per le opzioni CSV scelte dall'utente
  const [isImporting, setIsImporting] = useState(false); // Stato per feedback durante l'importazione locale
  const [isAddRasterDialogOpen, setIsAddRasterDialogOpen] = useState(false); // Stato per il dialogo AddRasterLayer via URL
  const [isAddVectorDialogOpen, setIsAddVectorDialogOpen] = useState(false); // Stato per il dialogo AddVectorLayer via URL

  const fileInputRef = useRef<HTMLInputElement>(null);
  // Seleziona addLayer dallo store Zustand
  const addLayer = useStoreWithEqualityFn(
    useGeoData,
    (state: GeoDataState) => state.addLayer, // Azione per registrare il layer nello store
    shallow
  );

  const handleButtonClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) {
      setSelectedFile(null);
      setCsvFileHeaders(null);
      setCsvOptions(undefined);
      e.target.value = '';
      return;
    }

    const file = files[0];
    setSelectedFile(file);
    setCsvFileHeaders(null); // Resetta gli header al cambio file
    setCsvOptions({ geometryType: null, isDMS: false }); // Resetta le opzioni CSV, inizializzando geometryType e isDMS

    const fileExtension = file.name.split('.').pop()?.toLowerCase();

    if (fileExtension === 'csv') {
      // Leggi header CSV per eventuale configurazione coordinate
      Papa.parse(file, {
        preview: 1, // Leggi solo la prima riga per gli header
        header: true, // Interpreta la prima riga come header
        skipEmptyLines: true,
        complete: (results) => {
          if (results.meta.fields && results.meta.fields.length > 0) {
            setCsvFileHeaders(results.meta.fields);
            // Non resettare csvOptions qui se l'utente ha già iniziato a configurare,
            // a meno che non sia un file completamente nuovo.
            // Il reset iniziale di csvOptions a { geometryType: null } dovrebbe bastare.
          } else {
            setCsvFileHeaders(null); // Nessun header trovato
            alert("Attenzione: Impossibile leggere gli header del CSV o il file è vuoto.");
          }
        },
        error: (error) => {
          console.error("Errore lettura header CSV:", error);
          setCsvFileHeaders(null);
          setCsvOptions(undefined); // Resetta opzioni se c'è errore
          alert(`Attenzione: Impossibile leggere gli header del CSV. L'importazione potrebbe richiedere configurazione manuale delle coordinate se necessaria.\n${error.message}`);
        }
      });
    } else {
      // Se non è un CSV, assicurati che gli stati relativi al CSV siano resettati
      setCsvFileHeaders(null);
      setCsvOptions(undefined);
    }
    // Pulisci l'input per permettere la riselezione
    e.target.value = '';
  };

  const handleImportClick = useCallback(async () => {
    if (!selectedFile) {
      alert("Per favore, seleziona un file.");
      return;
    }
    setIsImporting(true);

    try {
      // Passa direttamente csvOptions a processFiles.
      // processFiles è già stato aggiornato per accettare CsvProcessingOptions.
      await processFiles(
        [selectedFile],
        selectedLayerType,
        addLayer,
        selectedFile?.name.endsWith('.csv') ? csvOptions : undefined
      );
      // Feedback successo (opzionale, potrebbe essere gestito da store/notifiche)
      // alert(`${selectedFile.name} importato come layer ${selectedLayerType}.`);
      // Resetta stato dopo importazione successo
      setSelectedFile(null);
      setCsvFileHeaders(null);
      setCsvOptions(undefined);
      if (fileInputRef.current) fileInputRef.current.value = '';

    } catch (error) {
      const displayError = error instanceof Error ? error.message : "Errore sconosciuto";
      alert(`Errore durante l'importazione di ${selectedFile.name}.\n${displayError}\nControlla la console.`);
      console.error("Errore importazione:", error);
    } finally {
      setIsImporting(false);
    }
  }, [selectedFile, selectedLayerType, addLayer, csvOptions]); // Aggiunto csvOptions alle dipendenze

  const layerTypeOptions: LayerBase['maplibreLayerType'][] = ['fill', 'line', 'circle', 'symbol', 'heatmap', 'fill-extrusion']; // Usa il tipo corretto

  const selectInputOptions: SelectOption[] = layerTypeOptions.map(type => ({
    value: type,
    label: type.charAt(0).toUpperCase() + type.slice(1)
  }));

  const tabItems: TabItem[] = [
    {
      // id: 'local-storage', // Rimosso id non necessario per TabItem in Tabs.tsx
      label: 'Local',
      icon: <HardDrive size={16} />,
      content: (
        <div className={styles.tabContentContainer}>
          {/* Input File Nascosto */}
          <input
            ref={fileInputRef}
            type="file"
            accept=".geojson,.json,.csv,.gpx,.kml,.zip"
            style={{ display: 'none' }}
            onChange={handleFileChange}
          />

          {/* Pulsante Selezione File */}
          <div className={styles.buttonContainer}>
            <MenuButton
              onClick={handleButtonClick}
              disabled={isImporting}
              icon={<Upload size={16} />}
              className={styles.fullWidthButton}
            >
              {selectedFile ? `File: ${selectedFile.name}` : 'Layer from LocalStorage'}
            </MenuButton>
          </div>

          {/* Selettore Tipo Layer */}
          {selectedFile && (
            <div className={styles.formRow}>
              <label htmlFor="layerTypeSelect" className="input-label">
                Load as:
              </label>
              <div className={styles.selectInputContainer}>
                <SelectInput
                  id="layerTypeSelect"
                  value={selectedLayerType}
                  onChange={(e) => setSelectedLayerType(e.target.value as LayerBase['maplibreLayerType'])}
                  options={selectInputOptions}
                  disabled={isImporting}
                  // className="full-width-button" // Rimosso, gestito da selectInputContainer e stile SelectInput
                />
              </div>
            </div>
          )}

          {/* Controlli specifici per CSV */}
          {selectedFile && selectedFile.name.endsWith('.csv') && csvFileHeaders && csvOptions && (
            <div className={styles.csvOptionsContainer}>
              <div className={styles.formRow}>
                <label htmlFor="geometryTypeSelect" className="input-label">
                  Formato Coordinate:
                </label>
                <div className={styles.selectInputContainer}>
                  <SelectInput
                    id="geometryTypeSelect"
                    value={csvOptions.geometryType || ''}
                    onChange={(e) => {
                      const newType = e.target.value as 'latlon' | 'wkt' | '';
                      setCsvOptions(prev => ({
                        ...prev,
                        geometryType: newType === '' ? null : newType,
                        latField: '', // Resetta i campi specifici quando cambia il tipo
                        lonField: '',
                        wktField: '',
                        isDMS: false, // Resetta isDMS quando cambia il tipo di geometria
                      }));
                    }}
                    options={[
                      { value: '', label: 'Seleziona tipo...' },
                      { value: 'latlon', label: 'Latitudine/Longitudine' },
                      { value: 'wkt', label: 'WKT (Well-Known Text)' },
                    ]}
                    disabled={isImporting}
                  />
                </div>
              </div>

              {csvOptions.geometryType === 'latlon' && (
                <>
                  <div className={styles.formRow}>
                    <label htmlFor="latFieldSelect" className="input-label">
                      Colonna Latitudine:
                    </label>
                    <div className={styles.selectInputContainer}>
                      <SelectInput
                        id="latFieldSelect"
                        value={csvOptions.latField || ''}
                        onChange={(e) => setCsvOptions(prev => ({ ...prev!, latField: e.target.value }))}
                        options={csvFileHeaders.map(h => ({ value: h, label: h }))}
                        disabled={isImporting}
                      />
                    </div>
                  </div>
                  <div className={styles.formRow}>
                    <label htmlFor="lonFieldSelect" className="input-label">
                      Colonna Longitudine:
                    </label>
                    <div className={styles.selectInputContainer}>
                      <SelectInput
                        id="lonFieldSelect"
                        value={csvOptions.lonField || ''}
                        onChange={(e) => setCsvOptions(prev => ({ ...prev!, lonField: e.target.value }))}
                        options={csvFileHeaders.map(h => ({ value: h, label: h }))}
                        disabled={isImporting}
                      />
                    </div>
                  </div>
                  {/* Checkbox per formato DMS */}
                  <div className={styles.formRow}>
                    <label htmlFor="dmsCheckbox" className={`${styles.checkboxLabel} input-label`}>
                      <input
                        type="checkbox"
                        id="dmsCheckbox"
                        checked={csvOptions.isDMS || false}
                        onChange={(e) => setCsvOptions(prev => ({ ...prev!, isDMS: e.target.checked }))}
                        disabled={isImporting}
                        className={styles.checkboxInput}
                      />
                      Coordinate in formato Gradi/Minuti/Secondi (DMS)
                    </label>
                  </div>
                </>
              )}

              {csvOptions.geometryType === 'wkt' && (
                <div className={styles.formRow}>
                  <label htmlFor="wktFieldSelect" className="input-label">
                    Colonna WKT:
                  </label>
                  <div className={styles.selectInputContainer}>
                    <SelectInput
                      id="wktFieldSelect"
                      value={csvOptions.wktField || ''}
                      onChange={(e) => setCsvOptions(prev => ({ ...prev!, wktField: e.target.value }))}
                      options={csvFileHeaders.map(h => ({ value: h, label: h }))}
                      disabled={isImporting}
                    />
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Pulsante Importa */}
          <div className={styles.buttonContainer}>
            <MenuButton
              onClick={handleImportClick}
              disabled={
                !!( // Forza l'intera espressione a booleano
                  !selectedFile ||
                  isImporting ||
                  (selectedFile && selectedFile.name.endsWith('.csv') &&
                    csvFileHeaders &&
                    csvOptions &&
                    (
                      !csvOptions.geometryType ||
                      (csvOptions.geometryType === 'latlon' && (!csvOptions.latField || !csvOptions.lonField)) ||
                      (csvOptions.geometryType === 'wkt' && !csvOptions.wktField)
                    )
                  )
                )
              }
              icon={isImporting ? <Loader2 size={16} className={styles.animateSpin} /> : undefined}
              className={styles.fullWidthButton}
            >
              {isImporting ? 'Import...' : 'Import Layer'}
            </MenuButton>
          </div>
        </div>
      )
    },
    {
      label: "Remote",
      icon: <Cloud size={16} />,
      content: (
        <div className={styles.tabContentContainer}>
          <div className={styles.inputGroup}> {/* Raggruppa i bottoni */}
            <div className={styles.buttonContainer}>
              <MenuButton
                onClick={() => setIsAddRasterDialogOpen(true)}
                className={styles.fullWidthButton}
              >
                Raster Layer from URL
              </MenuButton>
            </div>
            <div className={styles.buttonContainer}>
              <MenuButton
                onClick={() => setIsAddVectorDialogOpen(true)}
                className={styles.fullWidthButton}
              >
                Vector Layer from URL
              </MenuButton> {/* Tag di chiusura mancante */}
            </div> {/* Tag di chiusura mancante per buttonContainer */}
          </div> {/* Tag di chiusura mancante per inputGroup */}

          {/* Dialoghi per l'aggiunta da URL */}
          <AddRasterLayerDialog
            isOpen={isAddRasterDialogOpen}
            onClose={() => setIsAddRasterDialogOpen(false)}
          />
          <AddVectorLayerDialog
            isOpen={isAddVectorDialogOpen}
            onClose={() => setIsAddVectorDialogOpen(false)}
          />
        </div>
      )
    },
  ];

  return (
    // Contenitore principale senza padding, il componente Tabs gestirà il proprio layout
    <div>
      <Tabs tabs={tabItems} />
    </div>
  );
};

export default ImportPanel;